import type { Agenda, AgendaActivity } from '@koderek/shared';
import { Alert, SimpleGrid, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import type { CSSProperties } from 'react';
import { ErrorAlert } from '../../components/ErrorAlert';
import { t } from '../../texts/pl';
import { formatTime } from '../editions/format';
import classes from './PersonPlan.module.css';
import { rejectedReason } from './reasons';
import type { EnrollmentControls } from './useEnrollment';

/**
 * US-408 AC-2, US-501 AC-1: zajęcia wybranej osoby rosnąco po godzinie – małe kafelki (godzina w kolorze rodzaju, tytuł)
 * w siatce do trzech kolumn, żeby nawet 9 zajęć nie spychało agendy w dół. Kliknięcie otwiera szczegóły.
 */
export function PersonPlan({
  agenda,
  controls,
  name,
  onOpen,
}: {
  agenda: Agenda;
  controls: EnrollmentControls;
  name: string;
  onOpen?: (activity: AgendaActivity) => void;
}) {
  const colors = new Map(agenda.types.map((ty) => [ty.id, ty.color]));
  const rooms = new Map(agenda.rooms.map((r) => [r.id, r.name]));
  const enrolled = agenda.activities
    .filter((a) => controls.items.get(a.id)?.state === 'enrolled')
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const rejected = rejectedReason(controls.error);

  return (
    <Stack gap="xs">
      <Title order={2} size="h5">
        {t.enrollment.planTitle(name)}
      </Title>
      {/* US-501 AC-3: odrzucony zapis – powód z serwera. */}
      {rejected ? (
        <Alert color="orange" role="alert">
          {rejected}
        </Alert>
      ) : controls.error ? (
        <ErrorAlert error={controls.error} />
      ) : null}
      {enrolled.length ? (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs" verticalSpacing="xs">
          {enrolled.map((a) => (
            <UnstyledButton
              key={a.id}
              className={classes.tile}
              onClick={() => onOpen?.(a)}
              disabled={!onOpen}
              title={`${formatTime(a.startsAt)}–${formatTime(a.endsAt)} · ${a.symbol} ${a.title} · ${rooms.get(a.roomId) ?? ''}`}
              style={{ '--type-color': colors.get(a.typeId) ?? 'var(--mantine-color-brand-6)' } as CSSProperties}
            >
              <span className={classes.time}>{formatTime(a.startsAt)}</span>
              <Text size="sm" fw={600} truncate>
                {a.title}
              </Text>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      ) : (
        <Text size="sm" c="dimmed">
          {t.enrollment.planEmpty}
        </Text>
      )}
    </Stack>
  );
}
